import React, { useEffect, useState } from "react";
import {
  Form,
  Input,
  Button,
  DatePicker,
  Select,
  Upload,
  Card,
  Row,
  Col,
  Table,
} from "antd";
import {
  DeleteOutlined,
  LinkOutlined,
  PlusOutlined,
  UploadOutlined,
} from "@ant-design/icons";
import PageTitle from "../PageTitle";
import { useParams } from "react-router-dom";
import useTenantNavigate from "../../hooks/useTenantNavigate";
import { useAxiosGet } from "../../hooks/useAxios";
import { toast } from "react-toastify";
import { postData } from "../../api/apiService";
import axios from "axios";
import FileUploadModal from "../FileUploadModal";
import moment from "moment";
import { FILE_PREFIX, getFileUrl } from "../../utils/constants";
import { CLIENTS, DOCUMENTS } from "../../api/endpointMap";
import { getEmbedToken } from "../../api/api";

const { Option } = Select;

const AddGSTNoticeForm = ({ serviceType }) => {
  const [form] = Form.useForm();
  const { clientId } = useParams();
  const navigate = useTenantNavigate();
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [uploadLoading, setUploadLoading] = useState(false);
  const [noticeLetter, setNoticeLetter] = useState(null);
  const [clientDetails, setClientDetails] = useState(null);
  const [loading, setLoading] = useState(false);

  const [fetchClientDetails] = useAxiosGet();

  const onFinish = async (values) => {
    console.log("Form Values:", values);

    const serviceEndpoint =
      serviceType == "gstin"
        ? "gstin"
        : serviceType == "tds"
        ? "tds"
        : "income-tax";

    const payload = {
      ...values,
      // GSTNotice model columns are client_id (FK) + client_name (label).
      // The form's `client` field is a readonly display of client_name; it
      // is not a model column, so strip it before sending.
      client_id: clientDetails?.id,
      client_name: clientDetails?.client_name,
      issue_date: values.issue_date
        ? values.issue_date.format("YYYY-MM-DD")
        : null,
      due_date: values.due_date ? values.due_date.format("YYYY-MM-DD") : null,
    };
    delete payload.client;

    try {
      setLoading(true);

      const data = await postData(DOCUMENTS.SAVE, {
        doctype: "GST Notice And Order",
        document_data: payload,
      });

      toast.info("Notice Added Successfully", {
        position: "top-right",
        autoClose: 5000,
      });

      setTimeout(() => {
        navigate(`/gstin/notices/${clientId}?disabledType=active`);
      }, 500);
    } catch (err) {
      toast.error(err, {
        position: "top-right",
        autoClose: 5000,
      });
      console.log("Error", err);
    } finally {
      setTimeout(() => {
        setLoading(false);
      }, 1000);
    }
  };

  const handleUpload = async (file) => {
    try {
      setUploadLoading(true);

      const payload = new FormData();
      payload.append("is_private", "0");
      // payload.append("doctype", "E Proceeding");
      // payload.append("docname", clientId);
      payload.append("file", file, file.name);

      const token = getEmbedToken() || localStorage.getItem("authToken") || "";
      const config = {
        headers: {
          "Content-Type": "multipart/form-data",
          Authorization: `Bearer ${token}`,
        },
      };

      const response = await axios.post(
        process.env.REACT_APP_API_BASE_URL + "/api/v1/files/upload",
        payload,
        config
      );

      const fileURL = response?.data?.file_url;

      if (fileURL) {
        form.setFieldsValue({ notice_letter: fileURL });
        setNoticeLetter(fileURL);
      }

      toast.info("File uploaded successfully", {
        position: "top-right",
        autoClose: 5000,
      });

      handleModalClose();
    } catch (error) {
      toast.error("Error uploading file", {
        position: "top-right",
        autoClose: 5000,
      });
      console.error("Error:", error);
    } finally {
      setUploadLoading(false);
    }
  };

  const handleModalClose = () => {
    setIsModalVisible(false);
  };

  useEffect(() => {
    fetchClientDetails({
      customURL: `/api/v1/clients/gst/${clientId}`,
      cb: (data) => {
        // GET /api/v1/clients/gst/{id} returns the client row flat (no
        // `result` wrapper) — match shape from clients.py:_gst_client_to_dict.
        setClientDetails(data || {});
      },
    });
  }, []);

  useEffect(() => {
    if (clientDetails) {
      form.setFieldValue("client", clientDetails?.client_name);
    }
  }, [clientDetails]);

  return (
    <div>
      <PageTitle title="New GST Notice And Order" />
      <div className="min-h-screen flex flex-col items-center justify-center bg-blue-100 p-4">
        <div className="w-full max-w-5xl bg-white shadow-lg rounded-2xl p-6">
          <Form
            form={form}
            layout="vertical"
            onFinish={onFinish}
            className="w-full"
          >
            <Card
              title="Notice Details"
              bordered={false}
              style={{ border: "4px solid #f5f5f5", marginBottom: "16px" }}
              headStyle={{ borderBottom: "3px solid #f5f5f5" }}
            >
              <Row gutter={[16, 16]}>
                <Col xs={24} sm={12}>
                  <Form.Item label="Client" name="client">
                    <Input readOnly />
                  </Form.Item>
                </Col>

                {/* <Col xs={24} sm={12}>
                  <Form.Item label="Company" name="company">
                    <Input />
                  </Form.Item>
                </Col> */}

                {/* <Col xs={24} sm={12}>
                  <Form.Item label="Notice/Demand Order ID" name="notice_id">
                    <Input />
                  </Form.Item>
                </Col> */}

                <Col xs={24} sm={12}>
                  <Form.Item label="Ref ID" name="ref_id">
                    <Input />
                  </Form.Item>
                </Col>

                <Col xs={24} sm={12}>
                  <Form.Item label="Description" name="description">
                    <Input />
                  </Form.Item>
                </Col>

                <Col xs={24} sm={12}>
                  <Form.Item label="Notice Status" name="notice_status">
                    <Select style={{ height: "42px" }}>
                      <Option value="Open">Open</Option>
                      <Option value="Close">Close</Option>
                    </Select>
                  </Form.Item>
                </Col>

                <Col xs={24} sm={12}>
                  <Form.Item label="Date of Issuance" name="issue_date">
                    <DatePicker
                      style={{ width: "100%" }}
                      format={"DD/MM/YYYY"}
                    />
                  </Form.Item>
                </Col>
                <Col xs={24} sm={12}>
                  <Form.Item label="Issued By" name="issued_by">
                    <Input />
                  </Form.Item>
                </Col>

                <Col xs={24} sm={12}>
                  <Form.Item label="Due Date" name="due_date" rules={[{ required: true, message: "Please select a due date" }]}>
                    <DatePicker
                      style={{ width: "100%" }}
                      format={"DD/MM/YYYY"}
                    />
                  </Form.Item>
                </Col>

                <Col xs={24} sm={12}>
                  <Form.Item label="Type" name="type">
                    <Input />
                  </Form.Item>
                </Col>
                <Col xs={24} sm={12}>
                  <Form.Item label="Amount Of Demand" name="amount">
                    <Input />
                  </Form.Item>
                </Col>
                {/* <Col xs={24} sm={12}>
                  <Form.Item
                    label="Notice/Demand Order Description"
                    name="description"
                  >
                    <Input />
                  </Form.Item>
                </Col> */}

                <Col xs={24} sm={12}>
                  <Form.Item label="Notice" name="notice_letter">
                    {noticeLetter ? (
                      <div className="flex items-center space-x-2">
                        <a
                          href={getFileUrl(noticeLetter)}
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          <Button icon={<LinkOutlined />}>
                            {" "}
                            <span>
                              {noticeLetter?.split("/")?.pop()?.split("?")[0]}
                            </span>
                          </Button>
                        </a>

                        <Button
                          icon={<DeleteOutlined />}
                          onClick={() => {
                            setNoticeLetter(null);
                            form.setFieldsValue({ notice_letter: "" });
                          }}
                        />
                      </div>
                    ) : (
                      <Button
                        onClick={() => {
                          setIsModalVisible(true);
                        }}
                        icon={<UploadOutlined />}
                      >
                        Attach
                      </Button>
                    )}
                  </Form.Item>
                </Col>
              </Row>
            </Card>

            <div className="flex justify-end mt-4">
              <Button loading={loading} type="primary" htmlType="submit">
                {loading ? "Adding.." : " Add Notice"}
              </Button>
            </div>
          </Form>
        </div>
      </div>

      <FileUploadModal
        visible={isModalVisible}
        onClose={handleModalClose}
        onUpload={handleUpload}
        disabled={uploadLoading}
      />
    </div>
  );
};

export default AddGSTNoticeForm;
